<template>
  <div class="auth-container">
    <div class="auth-card">
      <div class="auth-header">
        <img src="/lamusica-logo.svg" alt="LaMusica" class="auth-logo">
        <p>Reset your password</p>
      </div>
      
      <div v-if="submitted" class="success-state">
        <div class="success-icon">
          <AppIcon name="check-circle" />
        </div>
        <h3>Check your email</h3>
        <p>If an account exists for <strong>{{ email }}</strong>, you'll receive a password reset link shortly.</p>
        <p class="hint">The link will expire in 1 hour.</p>
        <router-link to="/login" class="btn-secondary">Back to Sign In</router-link>
      </div>
      
      <form v-else @submit.prevent="handleSubmit" class="auth-form">
        <p class="form-description">
          Enter your email address and we'll send you a link to reset your password.
        </p>
        
        <div class="form-group">
          <label for="email">Email</label>
          <input
            id="email"
            v-model="email"
            type="email"
            placeholder="you@example.com"
            required
            autofocus
          />
        </div>
        
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
        
        <button type="submit" class="btn-primary" :disabled="loading">
          {{ loading ? 'Sending...' : 'Send Reset Link' }}
        </button>
        
        <div class="back-link">
          <router-link to="/login"><AppIcon name="arrow-left" />Back to Sign In</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ForgotPasswordView',
  data() {
    return {
      email: '',
      error: null,
      loading: false,
      submitted: false
    }
  },
  methods: {
    async handleSubmit() {
      this.error = null
      this.loading = true
      
      try {
        const res = await fetch('/api/auth/forgot-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email })
        })
        
        const data = await res.json()
        
        if (!res.ok) {
          throw new Error(data.error || 'Failed to send reset link')
        }
        
        this.submitted = true
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gray-100);
  padding: 20px;
}

.auth-card {
  background: var(--white);
  border-radius: 8px;
  box-shadow: var(--shadow-xl);
  padding: 40px;
  width: 100%;
  max-width: 400px;
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.auth-logo {
  height: 40px;
  width: auto;
  margin-bottom: 12px;
}

.auth-header p {
  font-size: 14px;
  color: var(--gray-500);
  margin: 0;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-description {
  font-size: 14px;
  color: var(--gray-600);
  margin: 0;
  line-height: 1.5;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-700);
}

.form-group input {
  padding: 10px 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-group input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(51, 147, 255, 0.1);
}

.error-message {
  background: #fef2f2;
  color: var(--error);
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
}

.btn-primary {
  padding: 12px;
  background: var(--accent);
  color: var(--white);
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.back-link {
  text-align: center;
}

.back-link a {
  font-size: 13px;
  color: var(--gray-500);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.back-link a:hover {
  color: var(--accent);
}

.success-state {
  text-align: center;
}

.success-icon {
  margin-bottom: 16px;
}

.success-icon i {
  font-size: 48px;
  color: var(--success);
}

.success-state h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 12px 0;
}

.success-state p {
  font-size: 14px;
  color: var(--gray-600);
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.success-state .hint {
  font-size: 13px;
  color: var(--gray-500);
  margin-bottom: 24px;
}

.btn-secondary {
  display: inline-block;
  padding: 10px 20px;
  background: var(--white);
  color: var(--gray-700);
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.15s;
}

.btn-secondary:hover {
  background: var(--gray-50);
  border-color: var(--gray-400);
}
</style>
